export type CurrencyCode = 'GBP' | 'USD' | 'EUR'

export const CURRENCIES: { code: CurrencyCode; label: string }[] = [
  { code: 'GBP', label: 'Pounds (GBP, £)' },
  { code: 'USD', label: 'US dollars (USD, $)' },
  { code: 'EUR', label: 'Euros (EUR, €)' },
]

// Countries a tenant's Stripe connected account can be created in. Keep in sync with
// SupportedCountries.Codes on the backend.
export const STRIPE_COUNTRIES: { code: string; label: string }[] = [
  { code: 'GB', label: 'United Kingdom' },
  { code: 'US', label: 'United States' },
  { code: 'IE', label: 'Ireland' },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'NL', label: 'Netherlands' },
  { code: 'ES', label: 'Spain' },
  { code: 'IT', label: 'Italy' },
]

const LOCALES: Record<CurrencyCode, string> = { GBP: 'en-GB', USD: 'en-US', EUR: 'en-IE' }
const SYMBOLS: Record<CurrencyCode, string> = { GBP: '£', USD: '$', EUR: '€' }

export function normalizeCurrency(code?: string | null): CurrencyCode {
  const c = code?.trim().toUpperCase()
  return c === 'USD' || c === 'EUR' ? c : 'GBP'
}

export function currencySymbol(code?: string | null): string {
  return SYMBOLS[normalizeCurrency(code)]
}

export type FormatMoneyOptions = {
  /** Short form for chart axes, e.g. £1.2K */
  compact?: boolean
  /** Drop the pence/cents when the amount is a whole number, e.g. £1,200 rather than £1,200.00 */
  whole?: boolean
}

export function formatMoney(amount: number, currency?: string | null, opts: FormatMoneyOptions = {}): string {
  const code = normalizeCurrency(currency)
  if (opts.compact) {
    return new Intl.NumberFormat(LOCALES[code], {
      style: 'currency', currency: code, notation: 'compact', maximumFractionDigits: 1,
    }).format(amount)
  }
  const whole = opts.whole && Number.isInteger(amount)
  return new Intl.NumberFormat(LOCALES[code], {
    style: 'currency', currency: code,
    minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2,
  }).format(amount)
}

export type Money = {
  currency: CurrencyCode
  symbol: string
  fmt: (amount: number, opts?: FormatMoneyOptions) => string
}

/** Bundle of the symbol and a bound formatter for one currency. Pure (not a React hook). */
export function moneyFor(currency?: string | null): Money {
  const code = normalizeCurrency(currency)
  return { currency: code, symbol: currencySymbol(code), fmt: (n, o) => formatMoney(n, code, o) }
}
